import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import { ArrowLeft, Eye, Pencil, Trash2 } from 'lucide-react';
import { templatesApi } from '../../../api/templates.api';
import useConfirm from '../../../hooks/useConfirm';

const TemplateDraftsPage = ({ sess = { uid: '', role: 'user' } }) => {
  const theme = useTheme();
  const { confirm } = useConfirm();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      const response = await templatesApi.getDrafts();
      const list = response?.data?.data || response?.data || [];
      setDrafts(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    const isConfirmed = await confirm({
      title: 'Delete Draft',
      message: 'Are you sure you want to delete this draft?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      await templatesApi.deleteDraft(templateId);
      fetchDrafts();
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/templates" sx={secondaryButtonSx}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={700}>Template Drafts</Typography>
                <Typography variant="body2" color="text.secondary">
                  Continue drafts or clean up old ones
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          <Divider sx={{ mb: 2, opacity: 0.6 }} />
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : drafts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" color="text.secondary">
                        No drafts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  drafts.map((tpl, index) => (
                    <TableRow
                      key={tpl.t_id || tpl.id || index}
                      hover
                      sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{tpl.t_tname || '-'}</TableCell>
                      <TableCell>{tpl.t_language || '-'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{tpl.t_type || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" color="primary" href={`/templates/${tpl.t_id || tpl.id}`}>
                            <Eye size={16} />
                          </IconButton>
                          <IconButton size="small" color="primary" href={`/templates/edit/${tpl.t_id || tpl.id}`}>
                            <Pencil size={16} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(tpl.t_id || tpl.id)}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemplateDraftsPage;
